import React from "react";
import ReactDOM from "react-dom/client";

import { isEmbeddedInExtension } from "./bridge/types";
import { sendElationScrapeRecipe, signalReady } from "./bridge/extensionBridge";
import { msalInstance } from "./auth/msalConfig";
import { setSelectedProvider } from "./auth/authProviders";
import { ELATION_SCRAPE_RECIPE } from "./lib/elationScrapeRecipe";
import { stripVersionParam } from "./lib/appVersion";
import { purgeLegacyPHI } from "./lib/persistence";
import { applyThemeEarly } from "./theme/useDarkMode";
import { installPpApiMock } from "./dev/mockPpApi";
import App from "./App";
import "@astrana/ui/styles.css";
import "./index.css";

async function bootstrap() {
  // Apply the persisted theme before anything paints so a dark-preference
  // user doesn't see a light-mode flash while React hydrates.
  applyThemeEarly();

  // A version-triggered self-reload lands on `?_v=<version>` (see
  // refreshToLatest) purely to bypass a stale cached document. Drop it
  // before the router mounts so URLs stay clean; the hash is preserved for MSAL.
  const cleaned = stripVersionParam(window.location.href);
  if (cleaned !== null) window.history.replaceState(window.history.state, "", cleaned);

  // Strip any PHI left over in localStorage from prior builds that persisted
  // search filters / router state. Safe to run on every boot.
  purgeLegacyPHI();

  // TEMPORARY (AM-5619): "Part 1" and "Part 2" are both deployed on
  // providerPortalAPI dev, so nearly all of the native Create Claim module's
  // PP-API endpoints are real. Install the fetch mock UNCONDITIONALLY (as
  // before — the env-var/ENV_FILE gate proved unreliable in deployed builds),
  // but its route table now stubs ONLY the 3 endpoints that exist on NO
  // providerPortalAPI branch: ClaimMember/SearchClaimServiceCodes, GetVendors,
  // SearchCPTCodeByTypes. Every other Create Claim call falls through to the
  // real dev API. (AuthMember/SearchDiagCodes was never unbuilt — that route
  // is dead; the live shared endpoint is Utils/SearchDiagCodes.
  // GetClaimsFacilities became real in "Part 2".)
  // REMOVE this once those last 3 ship for real. See src/dev/mockPpApi.ts.
  installPpApiMock();

  // In standalone mode, handle the MSAL redirect response before React mounts.
  // This is load-bearing for the Entra login flow: sign-in uses loginRedirect
  // (see auth/AuthContext.tsx), so the user returns from Azure AD to this page
  // with the auth response in the URL hash. Consuming it here — before React
  // and the router mount — is what prevents hash_empty_error, since the router
  // ("/" → "/members") would otherwise strip the hash before MSAL reads it.
  // On a successful response we record the active provider so AuthContext
  // acquires a token for the returned account and treats the user as signed in.
  //
  // MSAL also persists its `interaction.status` flag in localStorage (because
  // cacheLocation is "localStorage" — see auth/msalConfig.ts). If a prior
  // interactive attempt was interrupted (tab closed mid-flow, network hiccup)
  // the flag can survive across page loads and make the next
  // handleRedirectPromise / loginRedirect throw hash_empty_error /
  // no_token_request_cache_error / interaction_in_progress with no real way to
  // recover except clearing it. Treat those three codes as stranded state and
  // wipe the cache so the next sign-in starts clean.
  if (!isEmbeddedInExtension()) {
    await msalInstance.initialize();
    try {
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        setSelectedProvider("entra");
      }
    } catch (err) {
      const code = (err as { errorCode?: string })?.errorCode;
      if (
        code === "hash_empty_error" ||
        code === "no_token_request_cache_error" ||
        code === "interaction_in_progress"
      ) {
        await msalInstance.clearCache();
      } else {
        console.error(err);
      }
    }
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );

  // In embedded mode, signal to the extension that we're ready for
  // messages and push the current Elation scrape recipe — the
  // sidepanel's content-script calls are recipe-driven as of
  // feat/recipe-driven-scraper, so it can't scrape anything until
  // this lands.
  if (isEmbeddedInExtension()) {
    signalReady();
    sendElationScrapeRecipe(ELATION_SCRAPE_RECIPE);
  }
}

bootstrap();
